import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Copy, KeyRound, RefreshCw, Check } from 'lucide-react';
import {
  PAYMENT_ENVIRONMENT_LABELS,
  PAYMENT_ENVIRONMENTS,
  type PaymentEnvironment,
  type PaymentGateway,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { ReadField } from '@/components/ContextPanel';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useBankAccounts } from '@/lib/queries';

/**
 * Ligação com o ASAAS — de onde saem o PIX, o boleto e o cartão.
 *
 * Duas coisas aqui não voltam depois de salvas: a chave da API e o segredo do
 * webhook. Quem tem a chave cobra em nome da empresa; quem tem o segredo dá
 * baixa nas contas. Ambos ficam criptografados, e a tela mostra só o
 * suficiente para reconhecer.
 */
export function CobrancaPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editando, setEditando] = useState(false);
  /// O segredo aparece UMA vez — logo depois de nascer ou de ser trocado.
  const [segredo, setSegredo] = useState<string | null>(null);
  const [rotacionando, setRotacionando] = useState(false);

  const query = useQuery({
    queryKey: ['payment-gateway'],
    queryFn: () => api.get<{ data: PaymentGateway | null }>('/payment-gateway'),
  });

  const gateway = query.data?.data ?? null;

  const rotateMutation = useMutation({
    mutationFn: () => api.post<{ webhookUrl: string; token: string }>('/payment-gateway/webhook-token'),
    onSuccess: (resposta) => {
      setRotacionando(false);
      setSegredo(resposta.token);
      void queryClient.invalidateQueries({ queryKey: ['payment-gateway'] });
    },
  });

  return (
    <>
      <WorkspaceHeader
        title="Cobrança"
        actions={
          can('gateway-pagamento:editar') ? (
            <Button variant="primary" icon={KeyRound} onClick={() => setEditando(true)}>
              {gateway ? 'Alterar ligação' : 'Ligar o ASAAS'}
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && !gateway && (
          <div className="card" style={{ padding: 20, maxWidth: 680 }}>
            <h3 style={{ margin: 0, fontSize: 15 }}>A cobrança automática ainda não está ligada</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
              Com o ASAAS ligado, cada parcela do contas a receber vira um link de pagamento com
              PIX, boleto e cartão. Quando o associado paga, a baixa cai sozinha na conta
              escolhida — ninguém precisa conferir extrato para dar baixa na mão.
            </p>
            <p className="muted" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
              Você vai precisar da <strong>chave da API</strong> do ASAAS (no painel deles, em
              Integrações). Comece pelo ambiente de <strong>teste</strong>: ele cobra de mentira, e
              é onde se confere que está tudo certo antes de cobrar gente de verdade.
            </p>
            {can('gateway-pagamento:editar') && (
              <div style={{ marginTop: 14 }}>
                <Button variant="primary" icon={KeyRound} onClick={() => setEditando(true)}>
                  Ligar o ASAAS
                </Button>
              </div>
            )}
          </div>
        )}

        {gateway && (
          <div style={{ display: 'grid', gap: 14, maxWidth: 680 }}>
            <div className="card" style={{ padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>ASAAS</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                  {gateway.environment === 'PRODUCAO' ? (
                    <StatusBadge kind="bad">Produção — cobra de verdade</StatusBadge>
                  ) : (
                    <StatusBadge kind="info">Teste (sandbox)</StatusBadge>
                  )}
                  {gateway.isActive ? (
                    <StatusBadge kind="ok">Ligada</StatusBadge>
                  ) : (
                    <StatusBadge kind="mute">Desligada</StatusBadge>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <ReadField label="Chave da API" value={gateway.apiKeyHint} mono />
                <ReadField label="Conta que recebe as baixas" value={gateway.bankAccountName} />
                <ReadField
                  label="Último aviso recebido"
                  value={gateway.lastEventAt ? formatDateTime(gateway.lastEventAt) : 'Nenhum ainda'}
                />
              </div>

              <div style={{ marginTop: 6 }}>
                <ReadField
                  label="Mensalidade do Clube"
                  value={
                    gateway.autoChargeMembership
                      ? 'cobrada automaticamente na geração'
                      : 'cobrança manual, pela parcela'
                  }
                />
              </div>

              {!gateway.isActive && (
                <p style={{ display: 'flex', gap: 8, fontSize: 12.5, color: T.amber, marginTop: 10 }}>
                  <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
                  Enquanto estiver desligada, nenhuma cobrança pode ser emitida.
                </p>
              )}
            </div>

            {/* O webhook é metade da integração: sem ele o dinheiro entra no
                ASAAS e o contas a receber nunca fica sabendo. */}
            <div className="card" style={{ padding: 18 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Aviso de pagamento (webhook)</h3>
              <p className="muted" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                No painel do ASAAS, em <strong>Integrações → Webhooks</strong>, cadastre a URL
                abaixo e cole o segredo no campo <strong>Token de autenticação</strong>. É por aqui
                que o pagamento vira baixa; sem isso, o dinheiro entra lá e o contas a receber não
                fica sabendo.
              </p>

              <div style={{ marginTop: 12 }}>
                <CampoParaCopiar rotulo="URL" valor={gateway.webhookUrl} />
              </div>

              {segredo ? (
                <div style={{ marginTop: 12 }}>
                  <CampoParaCopiar rotulo="Segredo" valor={segredo} />
                  <p style={{ display: 'flex', gap: 8, fontSize: 12.5, color: T.amber, marginTop: 8 }}>
                    <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
                    Copie agora: este segredo não aparece de novo. Se perder, gere outro — o
                    anterior para de valer na hora.
                  </p>
                </div>
              ) : (
                <p className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
                  O segredo foi mostrado quando a ligação foi criada e não volta mais.
                </p>
              )}

              {can('gateway-pagamento:editar') && (
                <div style={{ marginTop: 12 }}>
                  <Button small icon={RefreshCw} onClick={() => setRotacionando(true)}>
                    Gerar novo segredo
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <FormularioDaLigacao
        open={editando}
        gateway={gateway}
        onClose={() => setEditando(false)}
        onSaved={(token) => {
          setEditando(false);
          if (token) setSegredo(token);
          void queryClient.invalidateQueries({ queryKey: ['payment-gateway'] });
        }}
      />

      <Modal
        open={rotacionando}
        onClose={() => setRotacionando(false)}
        title="Gerar novo segredo do webhook"
        description="O segredo atual para de valer assim que o novo nascer"
        footer={
          <>
            <Button variant="outline" onClick={() => setRotacionando(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={rotateMutation.isPending}
              onClick={() => rotateMutation.mutate()}
            >
              Gerar novo
            </Button>
          </>
        }
      >
        {rotateMutation.isError && (
          <div className="login__alert" role="alert" style={{ marginBottom: 12 }}>
            {errorMessage(rotateMutation.error)}
          </div>
        )}
        <p style={{ fontSize: 13, lineHeight: 1.6 }}>
          Entre gerar o novo segredo e colá-lo no painel do ASAAS, os avisos de pagamento vão
          chegar com o segredo velho e ser <strong>recusados</strong> — o ASAAS reenvia depois, e
          nada se perde, mas a baixa atrasa. Deixe o painel deles aberto antes de continuar.
        </p>
      </Modal>
    </>
  );
}

/** Campo só de leitura com botão de copiar — o operador não digita isso à mão. */
function CampoParaCopiar({ rotulo, valor }: { rotulo: string; valor: string }) {
  const [copiado, setCopiado] = useState(false);

  return (
    <div>
      <div className="field__label" style={{ marginBottom: 4 }}>
        {rotulo}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input className="input input--mono" readOnly value={valor} style={{ flex: 1 }} />
        <Button
          small
          icon={copiado ? Check : Copy}
          onClick={() => {
            void navigator.clipboard?.writeText(valor);
            setCopiado(true);
            window.setTimeout(() => setCopiado(false), 2000);
          }}
        >
          {copiado ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
    </div>
  );
}

function FormularioDaLigacao({
  open,
  gateway,
  onClose,
  onSaved,
}: {
  open: boolean;
  gateway: PaymentGateway | null;
  onClose: () => void;
  onSaved: (webhookToken: string | null) => void;
}) {
  const accounts = useBankAccounts();

  const [environment, setEnvironment] = useState<PaymentEnvironment>('SANDBOX');
  const [apiKey, setApiKey] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [autoCharge, setAutoCharge] = useState(false);
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null);

  // Ajuste durante o render, como no FilterBar: sem efeito, sem render extra.
  const chave = open ? (gateway?.id ?? 'novo') : null;
  if (chave !== carregadoDe) {
    setCarregadoDe(chave);
    setEnvironment(gateway?.environment ?? 'SANDBOX');
    setApiKey('');
    setBankAccountId(gateway?.bankAccountId ?? '');
    setIsActive(gateway?.isActive ?? true);
    setAutoCharge(gateway?.autoChargeMembership ?? false);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      api.put<{ data: PaymentGateway; webhookToken: string | null }>('/payment-gateway', body),
    onSuccess: (resposta) => onSaved(resposta.webhookToken),
  });

  const errors = fieldErrors(mutation.error);
  const viraProducao = environment === 'PRODUCAO' && gateway?.environment !== 'PRODUCAO';
  // Trocar de ambiente EXIGE a chave do ambiente novo: a do outro não
  // funciona lá, e sem isto a ligação ficaria "ligada" com a chave errada,
  // recusando toda cobrança sem ninguém ligar uma coisa à outra. A API
  // recusa igual — a tela só evita a viagem.
  const trocouDeAmbiente = Boolean(gateway) && environment !== gateway!.environment;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={gateway ? 'Alterar a ligação com o ASAAS' : 'Ligar o ASAAS'}
      description="A chave fica criptografada e não volta para a tela"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={
              !bankAccountId ||
              ((!gateway || trocouDeAmbiente) && apiKey.trim().length < 10)
            }
            onClick={() =>
              mutation.mutate({
                provider: 'ASAAS',
                environment,
                ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
                bankAccountId,
                isActive,
                autoChargeMembership: autoCharge,
              })
            }
          >
            Salvar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 12 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Ambiente" htmlFor="gw-env" error={errors.environment}>
          <select
            id="gw-env"
            className="input"
            value={environment}
            onChange={(event) => setEnvironment(event.target.value as PaymentEnvironment)}
          >
            {PAYMENT_ENVIRONMENTS.map((value) => (
              <option key={value} value={value}>
                {PAYMENT_ENVIRONMENT_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        {viraProducao && (
          <p style={{ display: 'flex', gap: 8, fontSize: 12.5, color: T.amber, marginTop: -6 }}>
            <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
            Em produção as cobranças são reais e chegam a gente de verdade. Troque também a chave
            da API pela de produção — a de teste não funciona lá.
          </p>
        )}

        <Field
          label="Chave da API"
          htmlFor="gw-key"
          error={errors.apiKey}
          hint={
            trocouDeAmbiente
              ? 'Obrigatória: a chave do outro ambiente não funciona aqui'
              : gateway
                ? 'Em branco mantém a chave que já está guardada'
                : 'No painel do ASAAS, em Integrações → Chave de API'
          }
        >
          <input
            id="gw-key"
            className="input input--mono"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={gateway ? gateway.apiKeyHint : '$aact_…'}
          />
        </Field>

        <Field
          label="Conta que recebe as baixas"
          htmlFor="gw-account"
          error={errors.bankAccountId}
          hint="É nela que o pagamento do associado vai aparecer"
        >
          <select
            id="gw-account"
            className="input"
            value={bankAccountId}
            onChange={(event) => setBankAccountId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </Field>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Cobrança ligada
            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
              Desligada, nenhuma cobrança nova pode ser emitida. As que já existem continuam
              valendo e a baixa continua caindo.
            </span>
          </span>
        </label>

        {/* A chave que o Diretor desliga sozinho. É a única coisa nesta tela
            que faz o sistema pedir dinheiro a cliente SEM ninguém clicar. */}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={autoCharge}
            disabled={!isActive}
            onChange={(event) => setAutoCharge(event.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            Cobrar a mensalidade do Clube automaticamente
            <span className="muted" style={{ display: 'block', fontSize: 12 }}>
              Toda manutenção gerada já sai com a cobrança emitida, e o associado recebe o link
              sem ninguém clicar. Vale no ambiente escolhido acima — em produção, é dinheiro
              pedido a gente de verdade, todo dia às 04:10. Desmarque para voltar ao manual.
            </span>
          </span>
        </label>
        {autoCharge && environment === 'PRODUCAO' && (
          <p style={{ display: 'flex', gap: 8, fontSize: 12.5, color: T.amber }}>
            <AlertTriangle size={15} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
            Em produção e no automático: a partir de agora o sistema cobra os associados sozinho.
          </p>
        )}
      </FormSection>
    </Modal>
  );
}
